import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const MOBILE_MEDIA_QUERY = '(max-width: 749px)';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const optionValueId = String(event.detail?.optionValueId || '');
    const changedOption = optionValueId
      ? event.target.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"]`)
      : null;
    const shouldScrollToGallery =
      window.matchMedia(MOBILE_MEDIA_QUERY).matches &&
      event.target instanceof HTMLElement &&
      event.target.dataset.templateProductMatch === 'true' &&
      !event.target.closest('quick-add-dialog, featured-product-information') &&
      /^(color|colour)$/i.test(changedOption?.getAttribute('data-option-name')?.trim() || '');

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        if (!shouldScrollToGallery || !window.matchMedia(MOBILE_MEDIA_QUERY).matches) return;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const activeMediaGallery = newMediaGallery.isConnected
              ? newMediaGallery
              : document.querySelector('.product-information--jm media-gallery');
            if (!(activeMediaGallery instanceof HTMLElement)) return;

            const stickyHeader = document.querySelector('#header-component[sticky]');
            const headerOffset = stickyHeader instanceof HTMLElement ? stickyHeader.getBoundingClientRect().height : 0;
            const galleryTop = window.scrollY + activeMediaGallery.getBoundingClientRect().top;

            window.scrollTo({
              top: Math.max(0, galleryTop - headerOffset - 8),
              behavior: 'smooth',
            });
          });
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
